import { apiClient } from '../client';

export interface Metro {
  id: string;
  name: string;
  slug: string;
  neLatitude: string;
  neLongitude: string;
  swLatitude: string;
  swLongitude: string;
  searchRadius: number;
  timeZone: string;
  isActive: boolean;
  discoveryEnabled: boolean;
  regionCount: number;
  barCount: number;
  lastDiscoveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Region {
  id: string;
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  radius: number;
  time_zone: string;
  is_active: boolean;
  discovery_enabled: boolean;
  bar_count: number;
  metro_id: string | null;
  // Adaptive discovery columns
  discovered_bar_count?: number;
  hit_result_cap?: boolean;
  depth?: number;
  parent_region_id?: string | null;
}

export interface CreateMetroParams {
  name: string;
  slug: string;
  boundingBox: {
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
  };
  timeZone?: string;
}

export interface DiscoveryResults {
  totalRegions: number;
  apiCalls: number;
  depthDistribution: Record<number, number>;
  totalBars: {
    discovered: number;
    created: number;
    updated: number;
    skipped: number;
  };
  errors: Array<{ regionId?: string; error: string }>;
  durationMs: number;
}

export interface DiscoveryEstimate {
  totalRegions: number;
  activeRegions: number;
  activeBars: number;
  estimatedCalls: number;
  costPerCall: number;
  freeAllowance: number;
  estimatedCost: number;
  note: string;
}

export const metrosApi = {
  // List all metros
  list: async (): Promise<{ success: boolean; metros: Metro[] }> => {
    const response = await apiClient.get('/admin/metros');
    return response.data;
  },

  // Get single metro with regions
  get: async (id: string): Promise<{ success: boolean; metro: Metro; regions: Region[] }> => {
    const response = await apiClient.get(`/admin/metros/${id}`);
    return response.data;
  },

  // Create metro (regions created during adaptive discovery)
  create: async (params: CreateMetroParams): Promise<{ success: boolean; metro: Metro; estimate: { areaKm2: number; estimatedCalls: number } }> => {
    const response = await apiClient.post('/admin/metros', params);
    return response.data;
  },

  // Update metro
  update: async (id: string, data: Partial<Pick<Metro, 'name' | 'isActive' | 'discoveryEnabled'>>): Promise<{ success: boolean; metro: Metro }> => {
    const response = await apiClient.patch(`/admin/metros/${id}`, data);
    return response.data;
  },

  // Disable metro (soft delete)
  disable: async (id: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/admin/metros/${id}`);
    return response.data;
  },

  // Run adaptive discovery for metro
  discover: async (id: string): Promise<{ success: boolean; results: DiscoveryResults }> => {
    const response = await apiClient.post(`/admin/metros/${id}/discover`);
    return response.data;
  },

  // Get cost estimate for a specific metro
  getMetroEstimate: async (id: string): Promise<{ success: boolean; estimate: unknown }> => {
    const response = await apiClient.get(`/admin/metros/${id}/estimate`);
    return response.data;
  },

  // Get discovery cost estimate (all regions)
  getEstimate: async (): Promise<{ success: boolean; estimate: DiscoveryEstimate }> => {
    const response = await apiClient.get('/admin/discovery/estimate');
    return response.data;
  }
};
